import React, {
  forwardRef,
  useEffect,
  useState,
  useImperativeHandle,
  useCallback
} from 'react';
import useSkipUpdate from '../../hooks/useSkipRender';
import FormTOCView from '../view/FormTOCView';
import gsap from 'gsap';
import { ScrollToPlugin } from 'gsap/ScrollToPlugin';

gsap.registerPlugin(ScrollToPlugin);

const MIN_VIEW_WIDTH = 1440;

const FormTOCEngine = forwardRef(
  ({ fields = [], minWidth = MIN_VIEW_WIDTH }, ref) => {
    const [expanded, setExpanded] = useState(false);
    const [initialHiddenFields, setInitialHiddenFields] = useState([]);
    const [sortedSectionFields, setSortedSectionFields] = useState([]);
    const [currentSection, setCurrentSection] = useState(-1);
    const [isScreen2Small, setIsScreen2Small] = useState(
      window.innerWidth <= minWidth
    );
    // const sectionFields = fields.filter(one => one.uiType === 'section_break');
    const { shouldSkipUpdate, skipUpdate } = useSkipUpdate();

    const [visibleSections, setVisibleSections] = useState(sortedSectionFields);

    const handleResize = () => {
      setIsScreen2Small(window.innerWidth <= minWidth);
    };

    useEffect(() => {
      window.addEventListener('resize', handleResize);
      return () => {
        window.removeEventListener('resize', handleResize);
      };
    }, []);

    useEffect(() => {
      if (shouldSkipUpdate()) {
        return;
      }

      const sectionFields = fields.filter(
        one => one.uiType === 'section_break'
      );
      const newSortedSectionFields = sectionFields.sort((a, b) => {
        const aPos = a.position;
        const bPos = b.position;
        if (aPos < bPos) {
          return -1;
        } else if (aPos > bPos) {
          return 1;
        } else {
          return 0;
        }
      });
      setSortedSectionFields(newSortedSectionFields);
      setVisibleSections(
        sortedSectionFields.filter(
          one => initialHiddenFields.indexOf(one.internalId) === -1
        )
      );
    }, [fields, initialHiddenFields]);

    const toggleExpanded = () => {
      setExpanded(!expanded);
    };

    const initHiddenFields = useCallback(stateRef => {
      setInitialHiddenFields(stateRef.hiddenFields || []);
    }, []);

    const updateHiddenFields = useCallback(
      stateRef => {
        setVisibleSections(
          sortedSectionFields.filter(
            one => stateRef.hiddenFields.indexOf(one.internalId) === -1
          )
        );
      },
      [sortedSectionFields]
    );

    const onLinkClicked = useCallback(
      i => {
        setCurrentSection(i);
        const destinationSection = visibleSections[i];

        const frame = document.querySelector('section[id="forms"]');
        const elementIdSelector = `#internal-link-${destinationSection.internalId}`;

        gsap.to(frame, { duration: 1, scrollTo: elementIdSelector });
      },
      [visibleSections]
    );

    useImperativeHandle(
      ref,
      () => ({
        // isExpanded: expanded,
        initHiddenFields: initHiddenFields,
        updateHiddenFields: updateHiddenFields
      }),
      [initHiddenFields, updateHiddenFields]
    );

    const viewProps = {
      currentSection,
      expanded,
      toggleExpanded,
      sections: visibleSections,
      setCurrentSection,
      isScreen2Small,
      onLinkClicked
    };

    return sortedSectionFields.length ? <FormTOCView {...viewProps} /> : null;
  }
);

export default FormTOCEngine;
